import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import * as fromActions from './actions';
import { Store } from '@ngrx/store';
import { VehicleService } from '@services/api/api.service';
import { promiseWrapper } from '@shared/_funtions/promiseWarapper.function';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { AuthService } from '@auth0/auth0-angular';
import { environment } from '@env';
import { concat, from, of } from 'rxjs';
import {
  selectFiltersDealer,
  selectGeoviewDealerAutomaticRefreshEnabled,
  selectGeoviewDealerClusterTileCache,
  selectGeoviewDealerPage,
  selectGeoviewDealerPageSize,
  selectGeoviewDealerViewport,
  selectGeoviewDealerWithoutPositionFilterEnabled,
} from './index';
import { getVisibleClusterTiles, isClusterTileFresh } from './cluster-tiles';

@Injectable()
export class GeoviewDealerEffects {
  private userId: string;
  private isPollingActive = false;
  private pollingInterval: ReturnType<typeof setInterval>;
  private clusterTileRefreshInterval: ReturnType<typeof setInterval>;
  private hubConnections: HubConnection[] = [];
  private vehicleKeyStatus = new Map<string, string>();
  private readonly favoriteHubUrl = environment.sdfWsUrl + '/hubs/favorite';

  public startPolling$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.startPollingGeoviewDealer),
    withLatestFrom(this.store.select(selectGeoviewDealerAutomaticRefreshEnabled)),
    switchMap(([action, automaticRefreshEnabled]) => {
      const userId = action.userId;
      const sameUser = this.isPollingActive && this.userId === userId;
      const hasLiveConnections = this.hubConnections.some(
        (connection) =>
          connection?.state === HubConnectionState.Connected
          || connection?.state === HubConnectionState.Connecting,
      );

      if (sameUser && hasLiveConnections) {
        return [];
      }

      if (this.isPollingActive && this.userId && this.userId !== userId) {
        this.teardownHubConnections(this.userId);
      }

      this.isPollingActive = true;
      this.userId = userId;

      this.configureAutomaticRefresh(automaticRefreshEnabled);
      this.configureClusterTileRefresh(true);

      return [fromActions.obtainGeoviewDealerJwt()];
    }),
    switchMap(async (actions) => actions),
  ));

  public initialize$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.getGeoviewDealerVehicles),
    mergeMap(action => [
      fromActions.loadGeoviewDealerSummary(),
      fromActions.loadGeoviewDealerVehiclePage({ pageNumber: 1, pageSize: 50 }),
      fromActions.startPollingGeoviewDealer({ userId: action.user_id }),
    ]),
  ));

  public refresh$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.refreshGeoviewDealer),
    withLatestFrom(
      this.store.select(selectGeoviewDealerViewport),
      this.store.select(selectGeoviewDealerPage),
      this.store.select(selectGeoviewDealerPageSize),
    ),
    mergeMap(([, viewport, pageNumber, pageSize]) => [
      fromActions.loadGeoviewDealerSummary(),
      fromActions.loadGeoviewDealerVehiclePage({ pageNumber, pageSize }),
      ...(viewport ? [fromActions.loadGeoviewDealerClusterTiles({
        tiles: getVisibleClusterTiles(viewport),
        force: true,
      })] : []),
    ]),
  ));

  public refreshVisibleClusterTiles$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.refreshGeoviewDealerVisibleClusterTiles),
    withLatestFrom(this.store.select(selectGeoviewDealerViewport)),
    mergeMap(([, viewport]) => viewport
      ? [fromActions.loadGeoviewDealerClusterTiles({
        tiles: getVisibleClusterTiles(viewport),
      })]
      : []),
  ));

  public setAutomaticRefresh$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setGeoviewDealerAutomaticRefresh),
    tap(({ enabled }) => this.configureAutomaticRefresh(enabled)),
  ), { dispatch: false });

  public setWithoutPositionFilter$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setGeoviewDealerWithoutPositionFilter),
    withLatestFrom(this.store.select(selectGeoviewDealerPageSize)),
    map(([, pageSize]) => fromActions.loadGeoviewDealerVehiclePage({
      pageNumber: 1,
      pageSize,
    })),
  ));

  public setFilters$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setGeoviewDealerFilters),
    withLatestFrom(
      this.store.select(selectGeoviewDealerViewport),
      this.store.select(selectGeoviewDealerPageSize),
    ),
    mergeMap(([, viewport, pageSize]) => [
      fromActions.loadGeoviewDealerSummary(),
      fromActions.loadGeoviewDealerVehiclePage({ pageNumber: 1, pageSize }),
      ...(viewport ? [fromActions.loadGeoviewDealerClusterTiles({
        tiles: getVisibleClusterTiles(viewport),
      })] : []),
    ]),
  ));

  public setViewport$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.setGeoviewDealerViewport),
    map(({ viewport }) => fromActions.loadGeoviewDealerClusterTiles({
      tiles: getVisibleClusterTiles(viewport),
    })),
  ));

  public loadSummary$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.loadGeoviewDealerSummary),
    withLatestFrom(this.store.select(selectFiltersDealer)),
    switchMap(([, filters]) => this.vehicleService.getGeoviewSummary(
      filters.q,
      filters.vehicleStatus?.active || null,
      filters.vehicleStatus?.alarm || null,
      filters.vehicleStatus?.maintenance || null,
      filters.dataTraffic || null,
      filters.vehicleStatus?.star || null,
    ).pipe(
      map(summary => fromActions.loadGeoviewDealerSummarySuccess({ summary })),
      catchError(error => of(fromActions.loadGeoviewDealerSummaryFailure({ error }))),
    )),
  ));

  public loadClusterTiles$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.loadGeoviewDealerClusterTiles),
    withLatestFrom(
      this.store.select(selectFiltersDealer),
      this.store.select(selectGeoviewDealerClusterTileCache),
    ),
    switchMap(([{ tiles, force }, filters, cachedTiles]) => {
      const now = Date.now();
      const tilesToRequest = tiles.filter(tile => {
        const cachedTile = cachedTiles[tile.key];
        return force || !cachedTile || !isClusterTileFresh(cachedTile.status, cachedTile.loadedAt, now);
      });
      if (tilesToRequest.length === 0) {
        return [];
      }

      return concat(
        of(fromActions.geoviewDealerClusterTileRequestsStarted({ tiles: tilesToRequest })),
        from(tilesToRequest).pipe(
          mergeMap(tile => this.vehicleService.getVehicleClusterTile(
            tile.zoomLevel,
            tile.tileX,
            tile.tileY,
            filters.q,
            filters.vehicleStatus?.active || null,
            filters.vehicleStatus?.alarm || null,
            filters.vehicleStatus?.maintenance || null,
            filters.dataTraffic || null,
            filters.vehicleStatus?.star || null,
          ).pipe(
            map(result => fromActions.loadGeoviewDealerClusterTileSuccess({
              tile,
              clusters: result.clusters || [],
              loadedAt: Date.now(),
            })),
            catchError(error => of(fromActions.loadGeoviewDealerClusterTileFailure({ tile, error }))),
          ), 4),
        ),
      );
    }),
  ));

  public loadVehiclePage$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.loadGeoviewDealerVehiclePage),
    withLatestFrom(
      this.store.select(selectFiltersDealer),
      this.store.select(selectGeoviewDealerWithoutPositionFilterEnabled),
    ),
    switchMap(([
                 { pageNumber, pageSize, focusVin },
                 filters,
                 withoutPosition,
               ]) => this.vehicleService.getVehiclesGeoviewPaged(
      null,
      null,
      null,
      null,
      filters.q,
      filters.vehicleStatus?.active || null,
      filters.vehicleStatus?.alarm || null,
      filters.vehicleStatus?.maintenance || null,
      filters.dataTraffic || null,
      filters.vehicleStatus?.star || null,
      withoutPosition || null,
      focusVin || null,
      pageNumber,
      pageSize,
    ).pipe(
      map(page => fromActions.loadGeoviewDealerVehiclePageSuccess({ page })),
      catchError(error => of(fromActions.loadGeoviewDealerVehiclePageFailure({ error }))),
    )),
  ));

  public focusSelectedVehicleInPage$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.loadGeoviewDealerVehicleByVin),
    withLatestFrom(this.store.select(selectGeoviewDealerPageSize)),
    map(([{ cluster }, pageSize]) => fromActions.loadGeoviewDealerVehiclePage({
      pageNumber: 1,
      pageSize,
      focusVin: cluster.vin!,
    })),
  ));

  public loadVehicleByVin$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.loadGeoviewDealerVehicleByVin),
    switchMap(({ cluster }) => this.vehicleService.getVehicleDetailByVin(cluster.vin!).pipe(
      map(vehicle => fromActions.loadGeoviewDealerVehicleByVinSuccess({ cluster, vehicle })),
      catchError(error => of(fromActions.loadGeoviewDealerVehicleByVinFailure({
        vin: cluster.vin!,
        error,
      }))),
    )),
  ));

  public stopPolling$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.stopPollingGeoviewDealer),
    switchMap(() => {
      const userId = this.userId;
      this.isPollingActive = false;
      this.userId = undefined;

      this.configureAutomaticRefresh(false);
      this.configureClusterTileRefresh(false);

      this.teardownHubConnections(userId);

      return [];
    }),
  ), { dispatch: false });

  public obtainGeoviewDealerJwt$ = createEffect(() => this.actions$.pipe(
    ofType(fromActions.obtainGeoviewDealerJwt),
    switchMap(async () => {
      try {
        const accessToken = await promiseWrapper(this.auth.getAccessTokenSilently());

        if (!accessToken) {
          console.log('No access token available for Favorite WebSocket');
          return [];
        }

        this.teardownHubConnections(this.userId);

        const connection = new HubConnectionBuilder()
          .withUrl(`${this.favoriteHubUrl}?access_token=${accessToken}`)
          .withAutomaticReconnect()
          .build();

        connection.on('ReceivedMessage', (vehicleData: any) => {
          const keyStatus = this.extractKeyStatus(vehicleData);
          if (keyStatus != null) {
            this.handleKeyStatusChange(vehicleData.vin, keyStatus);
          }
        });

        this.hubConnections = [connection];

        const joinFavoriteGroup = async () => {
          await connection.invoke('JoinGroup', { userId: this.userId });
          console.log(`Favorite WebSocket joined group for user: ${this.userId}`);
        };

        const startConnection = async () => {
          try {
            await connection.start();
            await joinFavoriteGroup();
          } catch (err) {
            console.error('Favorite WebSocket connection error:', err);
            if (this.isPollingActive) {
              setTimeout(() => startConnection(), 5000);
            }
          }
        };

        connection.onreconnected(() => {
          console.log('Favorite WebSocket reconnected');
          if (this.isPollingActive) {
            joinFavoriteGroup().catch((err) => {
              console.error('Favorite WebSocket re-join group error:', err);
            });
          }
        });

        connection.onclose(() => {
          console.log('Favorite WebSocket connection closed');
          if (this.isPollingActive) {
            startConnection();
          }
        });

        await startConnection();

        return [];
      } catch (error) {
        console.error('Error setting up Favorite WebSocket connection:', error);
        return [];
      }
    }),
  ), { dispatch: false });

  private teardownHubConnections(userId?: string) {
    if (this.hubConnections.length === 0) {
      return;
    }

    this.hubConnections.forEach((connection, index) => {
      if (!connection) {
        return;
      }

      if (userId) {
        connection.invoke('LeaveGroup', userId).catch((e) => {
          console.error(`Error leaving favorite group for connection ${index}:`, e);
        });
      }

      connection.stop().catch((e) => {
        console.error(`Error stopping favorite hub connection ${index}:`, e);
      });
    });

    this.hubConnections = [];
    this.vehicleKeyStatus.clear();
  }

  private extractKeyStatus(vehicleData: any): string | null {
    const keyStatusTelemetry = vehicleData.telemetries?.find(
      (item) => item.category === 'keyStatus',
    );
    if (!keyStatusTelemetry) {
      return null;
    }

    const rawValue = keyStatusTelemetry.physicalValue ?? keyStatusTelemetry.physicalValueAsString;
    if (rawValue == null) {
      return null;
    }

    const normalized = rawValue.toString().trim().toUpperCase();
    if (normalized === 'ON' || normalized === 'OFF') {
      return normalized;
    }

    return null;
  }

  private handleKeyStatusChange(vin: string, keyStatus: string): void {
    const previousStatus = this.vehicleKeyStatus.get(vin);
    if (previousStatus !== undefined && previousStatus !== keyStatus) {
      this.store.dispatch(fromActions.dealerVehicleEngineStatusChanged({
        vin,
        engineOn: keyStatus === 'ON',
      }));
    }

    this.vehicleKeyStatus.set(vin, keyStatus);
  }

  private syncMetadata(): void {
    this.store.dispatch(fromActions.refreshGeoviewDealer());
  }

  private configureAutomaticRefresh(enabled: boolean): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }

    if (enabled && this.isPollingActive) {
      this.pollingInterval = setInterval(() => this.syncMetadata(), 30000);
    }
  }

  private configureClusterTileRefresh(enabled: boolean): void {
    if (this.clusterTileRefreshInterval) {
      clearInterval(this.clusterTileRefreshInterval);
      this.clusterTileRefreshInterval = null;
    }

    if (enabled && this.isPollingActive) {
      this.clusterTileRefreshInterval = setInterval(() => {
        this.store.dispatch(fromActions.refreshGeoviewDealerVisibleClusterTiles());
      }, 30000);
    }
  }

  constructor(
    private actions$: Actions,
    private store: Store,
    private vehicleService: VehicleService,
    private auth: AuthService,
  ) {
    this.userId = undefined;
  }
}
